import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import { FormikDatePicker } from 'globalcomponents/UI/Form/Base';
import CalendarToday from '@material-ui/icons/CalendarToday';
import { UI_DATE_LOWER_FORMAT } from 'globalconfig/constants/static';
import { hasRestrictedRole, getDateLocale } from 'globalutils/fmUtils';
import { ROLES } from 'globalconfig/constants/keys';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));

const RenderComponent = ({ values, handleEdit, validateForm, customProps, submitAction, cancelAction, errors, isNotValid }) => {
  const { i18n } = useTranslation();
  const isServiceManager = hasRestrictedRole([ROLES.SERVICE_MANAGER]);
  const SERVICE_PROVIDER = window.$environment.SERVICE_PROVIDER;

  return (
    <>
      {!(isServiceManager || hasRestrictedRole([ROLES.MECHANIC])) && (
        <>
          {!hasRestrictedRole([ROLES.DRIVER]) && (
            <FormStyle.default.FieldsWrapper className="csafield vertical">
              <Label variant="body1" htmlFor="driverId">
                {i18n.t('inspections.user')}
              </Label>
              <FormStyle.default.TextWrapper>
                <Field
                  name="driverId"
                  onChange={(event) => handleEdit(event, { field: 'driverId' })}
                  as={Select}
                  placeholder={checkServiceProvider('renderComp_driverId', SERVICE_PROVIDER, false)}
                  value={values['driverId']}
                  suggestions={customProps.userList}
                  isClearable
                />
                <Message type="error" message={customProps.validationError} />
              </FormStyle.default.TextWrapper>
            </FormStyle.default.FieldsWrapper>
          )}
          <FormStyle.default.FieldsWrapper className="csafield vertical">
            <Label variant="body1" htmlFor="vehicleId">
              {i18n.t('inspections.vehicle')}
            </Label>
            <FormStyle.default.TextWrapper>
              <Field
                name="vehicleId"
                onChange={(event) => handleEdit(event, { field: 'vehicleId' })}
                as={Select}
                placeholder={checkServiceProvider('renderComp_vehicleId', SERVICE_PROVIDER, false)}
                value={values['vehicleId']}
                suggestions={customProps.vehicleList}
                isClearable
              />
            </FormStyle.default.TextWrapper>
          </FormStyle.default.FieldsWrapper>
        </>
      )}
      {(isServiceManager || hasRestrictedRole([ROLES.MECHANIC])) && (
        <>
          <FormStyle.default.FieldsWrapper className="csafield vertical">
            <Label variant="body1" htmlFor="companyName">
              {i18n.t('inspections.searchBycompany')}
            </Label>
            <FormStyle.default.TextWrapper>
              <Field
                name="companyName"
                handleOnChange={(event) => handleEdit(event, { field: 'companyName' })}
                as={Input}
                type="text"
              />
              <Message type="error" message={customProps.validationError} />
            </FormStyle.default.TextWrapper>
          </FormStyle.default.FieldsWrapper>
          {!hasRestrictedRole([ROLES.MECHANIC]) && (
            <FormStyle.default.FieldsWrapper className="csafield vertical">
              <Label variant="body1" htmlFor="mechanicId">
                {i18n.t('inspections.searchByMechanic')}
              </Label>
              <FormStyle.default.TextWrapper>
                <Field
                  name="mechanicId"
                  onChange={(event) => handleEdit(event, { field: 'mechanicId' })}
                  as={Select}
                  placeholder={checkServiceProvider('renderComp_mechanicId', SERVICE_PROVIDER, false)}
                  value={values['mechanicId']}
                  suggestions={customProps.mechanicList}
                  isClearable
                />
              </FormStyle.default.TextWrapper>
            </FormStyle.default.FieldsWrapper>
          )}
        </>
      )}
      <FormStyle.default.FieldsWrapper className="csafield vertical">
        <Label variant="body1" htmlFor="startDate">
          {i18n.t('common.dateFrom')}
        </Label>
        <FormStyle.default.TextWrapper>
          <Field
            id="startDate"
            name="startDate"
            onChange={(event) => {
              customProps.setDateValidationError('');
              handleEdit(event, { field: 'startDate', type: 'date' });
            }}
            as={FormikDatePicker}
            locale={getDateLocale()}
            placeholderText={UI_DATE_LOWER_FORMAT}
            value={values.startDate}
            showPopperArrow={false}
            showTimeSelect={false}
            maxDate={new Date()}
            showDisabledMonthNavigation
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            isClearable
          />
          {window.$environment.CURRENT_LAYOUT === 'SpireonNew' && <CalendarToday />}
          <Message type="error" message={errors?.startDate} />
        </FormStyle.default.TextWrapper>
      </FormStyle.default.FieldsWrapper>
      <FormStyle.default.FieldsWrapper className="csafield vertical">
        <Label variant="body1" htmlFor="endDate">
          {i18n.t('common.dateTo')}
        </Label>
        <FormStyle.default.TextWrapper>
          <Field
            id="endDate"
            name="endDate"
            onChange={(event) => {
              customProps.setDateValidationError('');
              handleEdit(event, { field: 'endDate', type: 'date' });
            }}
            as={FormikDatePicker}
            locale={getDateLocale()}
            placeholderText={UI_DATE_LOWER_FORMAT}
            value={values.endDate}
            showPopperArrow={false}
            showTimeSelect={false}
            maxDate={new Date()}
            showDisabledMonthNavigation
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            isClearable
          />
          <Message type="error" message={customProps.dateValidationError} />
          {window.$environment.CURRENT_LAYOUT === 'SpireonNew' && <CalendarToday />}
          <Message type="error" message={errors?.endDate} />
        </FormStyle.default.TextWrapper>
      </FormStyle.default.FieldsWrapper>
      <FormStyle.default.ButtonsWrapper className="vertical-button">
        <Button
          type="submit"
          label={i18n.t('common.view')}
          onClick={(e, d) => {
            const validateResponse = validateForm();
            validateResponse.then((data) => {
              submitAction(data);
            });
          }}
        />
        <Button
          className=""
          type={'reset'}
          label={i18n.t('common.clear')}
          onClick={() => {
            cancelAction();
          }}
        />
      </FormStyle.default.ButtonsWrapper>
    </>
  );
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  hasApiStatus: PropTypes.object,
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
};

export default RenderComponent;
